import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import { Modal } from "@mui/material";
import { useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import CloseIcon from "@mui/icons-material/Close";
import {
  UpfrontPaymentButtonRow,
  UpfrontPaymentCloseIcon,
  UpfrontPaymentDesktopContainer,
  UpfrontPaymentOptional,
  UpfrontPaymentStripeInput,
  UpfrontPaymentStripeInputError,
  UpfrontPaymentStripeInputRow,
} from "./UpfrontPayment.styles";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { useBillPreviewsRefresh } from "../../utils/hooks/useBillPreviewsRefresh";
import { Button } from "../shared/Button/Button";
import { StyledButton } from "../shared/Button/Button.styles";
import {
  PostOA100PaymentSuccess,
  Bill,
  getCentsToDollars,
} from "./Services/UpfrontPaymentService";
import { BillingSupportNumber } from "./types";
import { getUserAuth } from "../../recoil/selectors";
import { validateEmail } from "../../utils/hooks/helpers";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { ErrorPopup } from "../shared/ErrorPopup/ErrorPopup";
import { SupportPhoneNumber } from "../../recoil/types";

export const OA100PaymentStripe: React.FC<{
  stripeModalOpen: boolean;
  setStripeModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setPaymentSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  setUpfrontBill: React.Dispatch<React.SetStateAction<Bill | undefined>>;
  clientSecret: string;
  paymentAmount: number;
  useNewCard: boolean;
}> = ({
  stripeModalOpen,
  setStripeModalOpen,
  clientSecret,
  paymentAmount,
  setPaymentSubmitted,
  setUpfrontBill,
  useNewCard,
}) => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const auth = useRecoilValue(getUserAuth);
  const refreshBillPreviews = useBillPreviewsRefresh();
  const { billingPK } = useParams<{ billingPK: string }>();

  const stripe = useStripe();
  const elements = useElements();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [stripeReady, setStripeReady] = useState(false);

  useEffect(() => {
    if (!clientSecret) {
      setErrorMessage(
        t("upfrontPayment.errors.contactForDetails", {
          phone: BillingSupportNumber,
        }),
      );
    }
  }, [clientSecret]);

  const handleSubmit: React.FormEventHandler = async (event) => {
    event.preventDefault();

    const supportMessage = t("upfrontPayment.errors.contactSupport", {
      phone: SupportPhoneNumber,
    });

    if (!stripe || !elements || !clientSecret) {
      setErrorMessage(
        t("upfrontPayment.errors.systemNotReady", {
          supportMessage,
        }),
      );
      return;
    }

    if (loading) {
      setErrorMessage(t("upfrontPayment.errors.systemLoading"));
      return;
    }

    setLoading(true);

    const paymentIntent = await stripe.retrievePaymentIntent(clientSecret);
    // Check that the payment amount from the API matches stripe
    if (paymentIntent.paymentIntent?.amount !== paymentAmount) {
      setErrorMessage(
        t("upfrontPayment.errors.validatingPayment", {
          supportMessage,
        }),
      );
      setLoading(false);
      return;
    }

    const elementsResult = await elements.submit();
    if (elementsResult.error) {
      setErrorMessage(`${elementsResult.error.message} ${supportMessage}`);
      setLoading(false);
      return;
    }

    setErrorMessage("");

    if (clientSecret) {
      const paymentMethodStripeResponse = await stripe.createPaymentMethod({
        elements,
        params: {
          metadata: {
            bill_pk: billingPK,
          },
        },
      });

      const oneTimePaymentStripeResponse = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: paymentMethodStripeResponse.paymentMethod?.id || "",
          setup_future_usage: "off_session",
        },
      );

      if (oneTimePaymentStripeResponse.error) {
        setErrorMessage(oneTimePaymentStripeResponse.error.message);
      } else if (
        oneTimePaymentStripeResponse.paymentIntent.status === "succeeded"
      ) {
        const oneTimePaymentSyncResponse = await PostOA100PaymentSuccess(
          billingPK,
          clientSecret,
          auth ? auth.email : email,
          paymentMethodStripeResponse.paymentMethod?.id,
        );
        if (oneTimePaymentSyncResponse?.ok) {
          const oneTimePaymentSyncJson =
            await oneTimePaymentSyncResponse.json();
          setUpfrontBill(oneTimePaymentSyncJson);
          setPaymentSubmitted(true);
          await refreshBillPreviews();
          setStripeModalOpen(false);
          setLoading(false);
        } else {
          setErrorMessage(
            t("upfrontPayment.errors.processingPayment", {
              supportMessage,
            }),
          );
        }
      }
      setLoading(false);
    }
  };

  return (
    <Modal
      open={stripeModalOpen}
      onClose={() => setStripeModalOpen(false)}
      style={{
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
        pointerEvents: loading ? "none" : undefined,
      }}
    >
      <UpfrontPaymentDesktopContainer deviceType={deviceType}>
        {errorMessage && (
          <ErrorPopup
            message={errorMessage}
            hideMessage={() => setErrorMessage("")}
          />
        )}
        {!useNewCard && <SpinnerLoading />}
        <form
          style={{
            display: useNewCard ? "grid" : "none",
          }}
          onSubmit={handleSubmit}
        >
          <UpfrontPaymentCloseIcon>
            <CloseIcon onClick={() => setStripeModalOpen(false)} />
          </UpfrontPaymentCloseIcon>
          {!auth && (
            <UpfrontPaymentStripeInputRow>
              <div>
                {" "}
                {t("upfrontPayment.stripe.email")}{" "}
                <UpfrontPaymentOptional>
                  {` ${t("upfrontPayment.stripe.optional")}`}{" "}
                </UpfrontPaymentOptional>
              </div>
              <UpfrontPaymentStripeInput
                placeholder={t("upfrontPayment.stripe.email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {email && !validateEmail(email) && (
                <UpfrontPaymentStripeInputError>
                  {" "}
                  {t("upfrontPayment.stripe.validEmail")}
                </UpfrontPaymentStripeInputError>
              )}
            </UpfrontPaymentStripeInputRow>
          )}
          {!stripeReady && <SpinnerLoading />}
          <PaymentElement
            options={{
              paymentMethodOrder: ["card"],
              layout: {
                type: "tabs",
              },
            }}
            onReady={() => setStripeReady(true)}
          />
          {loading && useNewCard ? (
            <SpinnerLoading />
          ) : (
            <>
              {" "}
              <UpfrontPaymentButtonRow>
                <StyledButton
                  type="submit"
                  size="medium"
                  $backgroundColor="greenDark"
                  disabled={
                    !stripe ||
                    !clientSecret ||
                    Boolean(email && !validateEmail(email))
                  }
                >
                  {t("upfrontPayment.actions.payAmount", {
                    amount: getCentsToDollars(paymentAmount),
                  })}
                </StyledButton>
              </UpfrontPaymentButtonRow>
              <UpfrontPaymentButtonRow bottomRow tertiary>
                <Button
                  label={t("upfrontPayment.actions.cancel")}
                  size="medium"
                  backgroundColor="greyMedium"
                  onClick={() => {
                    setErrorMessage("");
                    setStripeModalOpen(false);
                  }}
                  reverse
                />
              </UpfrontPaymentButtonRow>
            </>
          )}
        </form>
      </UpfrontPaymentDesktopContainer>
    </Modal>
  );
};
